// CmdInterpreter.js
/**
 * @fileoverview CmdInterpreter validates and interprets commands returned by agent API.
 */
import { UserActions } from 'BMapsCmds';
import { BackgroundColors } from 'BMapsSrc/themes';
import { App } from 'BMapsSrc/BMapsApp';
import { parseResidueSpec } from 'BMapsUtil/mol_info_utils';
import { ensureArray, conditionalArrayItem } from 'BMapsUtil/js_utils';
import { Compound, AtomGroup, AtomGroupTypes } from 'BMapsModel';
import { getCompoundForIndex, formatLabels } from './cmds_helpers';

// Validation information
const knownCommands = {
    SetView: {
        argInfo: [{
            options: ['protein', 'ligand', 'publication'],
        }],
    },
    ResetView: {
        argInfo: [],
    },
    SetDisplayStyle: {
        argInfo: [{
            options: ['default', 'wireframe', 'sticks', 'ballandstick', 'spacefill', 'cartoon', 'surface'],
        }],
    },
    SetHotspots: {
        argInfo: [{
            type: 'boolean',
        }],
    },
    SetColorScheme: {
        argInfo: [{
            options: ['default', ...Object.keys(BackgroundColors)],
        }],
    },
    Select: {
        argInfo: [{
            type: 'object',
        }],
        execute: async (entityRef={}) => {
            const { selectString, resSpec, cmpdIndex } = entityRef;
            let sel = selectString || resSpec || '';
            if (!sel) {
                if (cmpdIndex != null) {
                    const cmpd = getCompoundForIndex(Number(cmpdIndex));
                    sel = cmpd?.selectQuery || '';
                }
                if (!sel) {
                    console.warn(`Unable to form a select query for ${JSON.stringify(entityRef)}`);
                }
            }
            return UserActions.Select(sel);
        },
    },
    OpenDock: {
        argInfo: [{ type: 'array', optional: true }],
        execute: async (compoundRefs = []) => {
            if (compoundRefs.length === 0) {
                return UserActions.OpenDock();
            } else {
                const compounds = findAtomGroups(compoundRefs);
                return UserActions.OpenDock(compounds);
            }
        },
    },
    ExportCompound: {
        argInfo: [{ type: 'array', optional: true }],
        execute: async (compoundRefs = []) => {
            if (compoundRefs.length === 0) {
                return UserActions.OpenExport();
            } else {
                const compounds = findAtomGroups(compoundRefs);
                return UserActions.OpenExport({ compounds });
            }
        },
    },
    ActivateNextCompound: {
        argInfo: [{
            type: 'number',
        }],
    },
    PinCompound: {
        argInfo: [{ type: 'object' }, { type: 'boolean' }],
        execute: async (entityRef, shouldPin) => {
            const [cmpd] = findAtomGroups(entityRef);
            if (!cmpd) {
                console.log(`No compound found for entityRef: ${JSON.stringify(entityRef)}`);
                return null;
            }
            console.log('PinCompound calls on:', cmpd, `with pin: ${shouldPin}`);
            return UserActions.ToggleVisibility(
                cmpd,
                shouldPin,
            );
        },
    },
    StarCompound: {
        argInfo: [{ type: 'object' }, { type: 'boolean' }],
        execute: async (entityRef, shouldStar) => {
            const [cmpd] = findAtomGroups(entityRef);
            if (!cmpd) {
                console.log(`No compound found for entityRef: ${JSON.stringify(entityRef)}`);
                return null;
            }
            console.log('StarCompound calls on:', cmpd, `with star: ${shouldStar}`);
            return UserActions.ToggleStarred(
                cmpd,
                shouldStar,
            );
        },
    },
    EnergyMinimize: {
        argInfo: [{ type: [{ type: 'object' }], optional: true }],
        execute: async (input) => {
            // No arg case is like clicking "Energy Minimize" in the Right Control Buttons
            if (!input || (input.length != null && input.length === 0)) {
                return UserActions.RequestMinimize();
            }

            // Specifically minimizing one or more compounds
            const compounds = findAtomGroups(input);
            return UserActions.EnergyMinimize(compounds);
        },
    },
    SetFocusCompound: {
        argInfo: [{ type: 'object' }],
        execute: async (entityRef) => {
            const [cmpd] = findAtomGroups(entityRef);
            if (!cmpd) {
                console.log(`No compound matching ${JSON.stringify(entityRef)}`);
                return;
            }
            App.Workspace.activateCompound(cmpd);
        },
    },
    SetCustomAtomGroupStyle: {
        argInfo: [{ type: 'object' }, { type: 'object' }],
        execute: async (entityRef, styleObj) => {
            const atomGroups = findAtomGroups(entityRef);
            if (atomGroups.length === 0) {
                console.log(`No atom groups matching ${JSON.stringify(entityRef)}`);
                return;
            }
            atomGroups.forEach((group) => UserActions.SetCustomAtomGroupStyle(group, styleObj));
            UserActions.RedrawScene();
        },
    },
    CopyCompound: {
        argInfo: [{ type: 'object' }],
        execute: async (entityRef) => {
            const disallowedTypes = [
                AtomGroupTypes.ComputedWater, AtomGroupTypes.Fragment, AtomGroupTypes.CrystalWater,
            ];
            const atomGroups = findAtomGroups(entityRef)
                .flatMap((ag) => (ag.getResidues ? ag.getResidues() : ag))
                .filter((ag) => !disallowedTypes.includes(ag.type));
            if (atomGroups.length === 0) {
                console.log(`No atom groups matching ${JSON.stringify(entityRef)} for CopyCompound`);
                return { compounds: [], errors: ["Couldn't find the compound/residues to copy"] };
            }
            if (atomGroups.length === 1 && atomGroups[0] instanceof Compound) {
                return UserActions.CopyCompound(atomGroups[0]);
            } else {
                return UserActions.CopyResiduesToCompound(atomGroups);
            }
        },
    },
};

/**
 * Find atom groups meeting the provided residue specification.
 * @param {object | Array<object>} filters - an object containing filtering properties
 * @param {string} [filters.resSpec] - Residue specification string (e.g., "GLU.217:A").
 * @param {string} [filters.resn] - Residue name (e.g., 'GLU').
 * @param {number} [filters.resi] - Residue sequence number (e.g., 217).
 * @param {string} [filters.chain] - Chain ID (e.g., 'A').
 * @param {string} [filters.insCode] - Insertion code.
 * @param {string} [filters.altLoc] - Alternate location.
 * @param {number} [filters.modelNum] - Model number.
 * @param {number} [filters.cmpdIndex] - Compound index (e.g., 0, 1, 2...).
 * @returns {Array<object>} An array of matching atom group objects.
 */
function findAtomGroups(filters) {
    if (Array.isArray(filters)) {
        return filters.flatMap((item) => {
            const res = findAtomGroups(item);
            if (res.length === 0) {
                console.warn(`CmdInterpreter.findAtomGroups: nothing found for ${JSON.stringify(item)}`);
            }
            return res;
        });
    }

    const {
        resSpec, resn, resi, chain, insCode, altLoc, modelNum, cmpdIndex,
    } = filters;
    console.log('find atomgroups called with:', {
        resSpec, resn, resi, chain, insCode, altLoc, modelNum, cmpdIndex,
    });
    if (resSpec) {
        const parsedSpec = parseResidueSpec(resSpec);
        if (parsedSpec) {
            return findAtomGroups(parsedSpec);
        } else {
            console.warn(`Could not parse residue spec: "${resSpec}"`);
        }
    }
    if (cmpdIndex != null) {
        const cmpdIndexNum = Number(cmpdIndex);
        const compound = getCompoundForIndex(cmpdIndexNum);
        if (!compound) {
            console.warn(`No compound found for index ${cmpdIndexNum}`);
            return [];
        }
        return [compound];
    }

    // Here we should have resn, resi, chain, insCode, altLoc, modelNum
    // At least one of the spec components must be provided.
    const emptySpec = !resn && resi == null && !chain && !insCode && !altLoc && modelNum == null;
    if (emptySpec) {
        return [];
    }

    let found = App.Workspace.allCaseData().flatMap((cd) => cd.allAtomGroups()).filter((ag) => (
        (!resn || ag.resn === resn)
        && (resi == null || ag.resi === resi)
        && (!chain || ag.chain === chain)
        && (!insCode || ag.insCode === insCode)
        && (!altLoc || ag.altLoc === altLoc)
        && (modelNum == null || ag.modelNum === modelNum)
    ));

    // The resSpec of polymers (chains) match the first residue, so:
    //   1. If only chain is requested, and there is a polymer in the results, return only polymer.
    //   2. If not only chain, exclude polymers from the results.
    const onlyChain = chain && !resn && resi == null && !insCode && !altLoc; // exclude modelNum
    const anyPolymer = found.some((ag) => AtomGroup.PolymerTypes.includes(ag.type));
    found = found.filter((ag) => (
        (onlyChain && (!anyPolymer || AtomGroup.PolymerTypes.includes(ag.type)))
        || (!onlyChain && !AtomGroup.PolymerTypes.includes(ag.type))
    ));

    return found;
}

function getCommandLabel(action, args) {
    switch (action) {
        case 'SetView': return `Set the view to ${args[0]}`;
        case 'ResetView': return 'Reset view';
        case 'SetDisplayStyle': return `Set display style to ${args[0]}`;
        case 'SetHotspots': return `Set hotspots ${args[0] ? 'On' : 'Off'}`;
        case 'SetColorScheme': return `Set background color ${args[0]}`;
        case 'Select': {
            const [spec] = args;
            const label = formatLabels(spec);
            return `Select ${label}`;
        }
        case 'RequestMinimize': return 'Minimize compound';
        case 'PinCompound': {
            const [entityRef, shouldPin] = args;
            return `${shouldPin ? 'Pin' : 'Unpin'} ${formatLabels(entityRef)}`;
        }
        case 'StarCompound': {
            const [entityRef, shouldStar] = args;
            return `${shouldStar ? 'Star' : 'Unstar'} ${formatLabels(entityRef)}`;
        }
        case 'EnergyMinimize': {
            const argArray = ensureArray(args);
            const label = argArray.length > 0 ? argArray.map(formatLabels).join(', ') : 'compound';
            return `Minimize ${label}`;
        }
        case 'SetFocusCompound': {
            const [spec] = args;
            const label = formatLabels(spec);
            return `Focus on ${label}`;
        }
        case 'ActivateNextCompound': {
            const step = args[0];
            return `Step focus compound ${step < 0 ? step : `+${step}`}`;
        }
        case 'SetCustomAtomGroupStyle': {
            const [spec, style] = args;
            const label = formatLabels(spec);
            const parts = Object.entries(style).map(([key, value]) => {
                switch (key) {
                    case 'molStyle': return `molecular style ${value}`;
                    case 'color': return `color ${value}`;
                    case 'colorAllAtoms': return `color all atoms ${value ? 'on' : 'off'}`;
                    case 'showLabel': return `label ${value ? 'on' : 'off'}`;
                    default: return '';
                }
            }).filter(Boolean);
            return `Style ${label}: ${parts.join(', ')}`;
        }
        case 'OpenDock': {
            if (!args[0] || args[0].length === 0) {
                return 'Open the docking panel';
            }
            const compounds = formatLabels(args[0]);
            return `Dock ${compounds}`;
        }
        case 'ExportCompound': {
            if (!args[0] || args[0].length === 0) {
                return 'Export';
            }
            const compounds = formatLabels(args[0]);
            return `Export ${compounds}`;
        }
        case 'CopyCompound': {
            return `Copy ${formatLabels(args[0])}`;
        }
        default: return action;
    }
}

export default class CmdInterpreter {
    /**
     * Apply a command array returned by agent API.
     * This validates and executes each command representation.
     * @param {Array<[string, ...]>} cmdArray
     * @returns {{ results: [{ commandUnit, result, errors, displayText}}], error: string? }}
     */
    static async interpretCommand(cmdArray) {
        const results = { results: [], error: null };
        if (!Array.isArray(cmdArray) || cmdArray.length === 0) {
            results.error = 'Could not interpret command from words';
            return results;
        }
        console.log(`Applying interpreted command from words: ${JSON.stringify(cmdArray)}`);
        for (const unit of cmdArray) {
            const { action, args, error } = CmdInterpreter.validateCommand(unit);
            if (error) {
                const msg = `Validation error: ${error}`;
                console.log(msg);
                results.results.push({
                    commandUnit: unit,
                    errors: [msg],
                    displayText: CmdInterpreter.printCommandInfo(unit),
                });
                continue;
            }
            const cmdDef = knownCommands[action];
            let result;
            if (cmdDef.execute) {
                result = await cmdDef.execute(...args);
            } else {
                result = await UserActions[action](...args);
            }

            const errors = [];
            if (result?.error) errors.push(result?.error);
            if (result?.errors) errors.push(...result?.errors);

            results.results.push({
                commandUnit: unit,
                result,
                errors,
                displayText: CmdInterpreter.printCommandInfo(unit, errors),
            });
        }
        return results;
    }

    /**
     * Validates a command action and arguments, returning them if valid.
     * @param {[string, ...]} cmdUnit
     * @returns {{ action: string, args: [] } | { error: string }}
     */
    static validateCommand(cmdUnit) {
        if (!Array.isArray(cmdUnit) || cmdUnit.length === 0) {
            return { error: 'Invalid command' };
        }
        const [action, ...args] = cmdUnit;
        const { argInfo } = knownCommands[action] || {};
        if (!argInfo) {
            return { error: `Invalid action ${action}` };
        }
        // Optional arguments
        const requiredArgs = argInfo.filter((info) => !info.optional);
        const totalArgs = argInfo.length;
        if (args.length < requiredArgs.length || args.length > totalArgs) {
            const expectedDesc = requiredArgs.length === totalArgs
                ? `${totalArgs}`
                : `${requiredArgs.length}-${totalArgs}`;
            return { error: `Invalid number of arguments for ${action}: ${args.length} provided, ${expectedDesc} expected` };
        }
        // In case of missing optional arguments
        const paddedArgs = [...args];
        while (paddedArgs.length < argInfo.length) {
            paddedArgs.push(undefined);
        }
        for (let i = 0; i < paddedArgs.length; i++) {
            if (paddedArgs[i] !== undefined || !argInfo[i].optional) {
                const error = CmdInterpreter.validateArg(paddedArgs[i], argInfo[i]);
                if (error) return { error: `Invalid argument ${i} for ${action}: ${error}` };
            }
        }
        return { action, args: paddedArgs };
    }

    /**
     * Validate a single command argument
     * @param {any} arg
     * @param {{ type: string?, options: []?}} argInfo
     * @returns {string?} error string or null if successful
     */
    static validateArg(arg, argInfo) {
        const { type, options, optional } = argInfo;
        // Handle optional arguments that are undefined
        if (arg === undefined && optional) {
            return null;
        }

        if (Array.isArray(type)) {
            if (type.some((argInfo2) => !CmdInterpreter.validateArg(arg, argInfo2))) {
                // If any of the potential arg types returns falsy, it passes the validation
                return null;
            } else {
                return `Provided type ${typeof arg} doesn't match allowed list of types`;
            }
        }

        if (type) {
            const provided = typeof arg;
            switch (type) {
                case 'string':
                    // Note: eslint enforces always comparing typeof results to string literals
                    if (provided !== 'string') return `${type} required, ${provided} provided`;
                    break;
                case 'boolean':
                    if (provided !== 'boolean') return `${type} required, ${provided} provided`;
                    break;
                case 'object':
                    if (provided !== 'object' || arg == null) return `${type} required, ${provided} provided`;
                    break;
                case 'number':
                    if (provided !== 'number') return `${type} required, ${provided} provided`;
                    break;
                case 'array':
                    if (!Array.isArray(arg)) return `${type} required, ${provided} provided`;
                    break;
                // no default
            }
        }
        if (options) {
            if (!options.includes(arg)) return 'invalid option';
        }
        return null;
    }

    static printCommandInfo(cmdUnit, errors=[]) {
        if (!Array.isArray(cmdUnit) || cmdUnit.length === 0 || !knownCommands[cmdUnit[0]]) {
            return 'Invalid command';
        }
        const [action0, ...args0] = cmdUnit;
        const { action, args, error: validationError } = CmdInterpreter.validateCommand(cmdUnit);
        const label = getCommandLabel(action || action0, args || args0);

        const errMsg = [
            ...conditionalArrayItem(validationError || errors?.length > 0, 'failed'),
            ...conditionalArrayItem(validationError, 'invalid parameters'),
        ].join(', ');
        return errMsg ? `${label} (${errMsg})` : label;
    }
}
